import React, { useState, useEffect } from 'react';
import { Calendar, Power, RefreshCw, CheckCircle, XCircle, AlertCircle, ExternalLink, UserCheck } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const StaffCalendarSync = ({ staff, canManage, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [connected, setConnected] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [allowIndividual, setAllowIndividual] = useState(true);

  const loadStatus = async () => {
    setLoading(true);
    try {
      // Vérifier si l'intégration est activée globalement
      const globalResponse = await axios.get(`${API_BASE}/settings/integrations/google/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setGlobalEnabled(globalResponse.data.enabled);
      setAllowIndividual(globalResponse.data.allow_individual_connections !== false);

      // Vérifier le statut de l'employé
      const statusResponse = await axios.get(`${API_BASE}/users/${staff.id}/google-calendar/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setAuthorized(statusResponse.data.authorized || false);
      setConnected(statusResponse.data.connected || false);
      setLastSync(statusResponse.data.last_sync ? new Date(statusResponse.data.last_sync) : null);
    } catch (error) {
      console.error('Error loading status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/admin/users/${staff.id}/google-calendar/authorize`, {
        authorized: !authorized
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      await loadStatus();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating authorization:', error);
      alert(error.response?.data?.detail || 'Error updating authorization');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/google/url`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.data.url) {
        sessionStorage.setItem('google_calendar_user_id', staff.id);
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error getting auth URL:', error);
      alert('Error starting Google authentication');
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm(`Remove Google Calendar connection for ${staff.first_name} ${staff.last_name}?`)) {
      setLoading(true);
      try {
        await axios.post(`${API_BASE}/auth/google/disconnect`, {
          user_id: staff.id
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        await loadStatus();
        alert('Google Calendar disconnected');
      } catch (error) {
        console.error('Error disconnecting:', error);
        alert('Error disconnecting');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/calendar/google/sync`, {
        user_id: staff.id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      alert(`Synced ${response.data.synced_count || 0} events`);
      await loadStatus();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Error syncing events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, [staff.id]);

  // Si l'intégration globale est désactivée
  if (!globalEnabled) {
    return (
      <div className="bg-gray-900/50 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-500">Google Calendar integration disabled</span>
        </div>
      </div>
    );
  }

  // Si les connexions individuelles sont désactivées
  if (!allowIndividual) {
    return (
      <div className="bg-gray-900/50 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-500">Individual connections disabled by admin</span>
        </div>
      </div>
    );
  }

  const getStatusIcon = () => {
    if (loading) return <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />;
    if (connected) return <CheckCircle className="w-4 h-4 text-green-400" />;
    if (authorized && !connected) return <AlertCircle className="w-4 h-4 text-yellow-400" />;
    return <XCircle className="w-4 h-4 text-gray-500" />;
  };

  const getStatusText = () => {
    if (connected) return 'Connected';
    if (authorized && !connected) return 'Pending connection';
    return 'Disabled';
  };

  return (
    <div className="bg-gray-900/50 rounded-lg p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-gray-300">Google Calendar</span>
          <div className="flex items-center gap-1 ml-2">
            {getStatusIcon()}
            <span className="text-xs text-gray-400">{getStatusText()}</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          {/* Admin: Activer/désactiver la permission */}
          {canManage && (
            <button
              onClick={handleAuthorize}
              disabled={loading}
              className={`px-2 py-1 rounded text-xs transition ${
                authorized
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
              }`}
            >
              <Power className="w-3 h-3 inline mr-1" />
              {authorized ? 'Disable' : 'Enable'}
            </button>
          )}
          
          {/* Utilisateur: Connecter son calendrier */}
          {authorized && !connected && (
            <button
              onClick={handleConnect}
              className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30 transition"
            >
              <ExternalLink className="w-3 h-3 inline mr-1" />
              Connect
            </button>
          )}
          
          {/* Utilisateur: Déconnecter */}
          {connected && (
            <button
              onClick={handleDisconnect}
              disabled={loading}
              className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs hover:bg-gray-600 transition"
            >
              Disconnect
            </button>
          )}
          
          {/* Bouton Sync */}
          {connected && (
            <button
              onClick={handleSync}
              disabled={loading}
              className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs hover:bg-gray-600 transition"
            >
              <RefreshCw className="w-3 h-3 inline mr-1" />
              Sync
            </button>
          )}
        </div>
      </div>
      
      {/* Dernière synchronisation */}
      {lastSync && (
        <div className="text-xs text-gray-500 mt-2">
          Last sync: {lastSync.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default StaffCalendarSync;